import { Injectable, WritableSignal } from '@angular/core';

export interface ErrorHandlingConfig {
  showValidationDetails?: boolean;
  logToConsole?: boolean;
  genericMessages?: {
    validation?: string;
    server?: string;
    network?: string;
    unknown?: string;
  };
}

export interface ErrorState {
  serverError: WritableSignal<string>;
  fieldErrors: WritableSignal<Record<string, string>>;
  isLoading: WritableSignal<boolean>;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlingService {

  /**
   * Handles HTTP errors and returns appropriate user-friendly messages
   * @param error The HTTP error response
   * @param context Optional context for logging (e.g., component name)
   * @param config Optional configuration for error handling behavior
   * @returns Object with error message and field errors
   */
  handleHttpError(
    error: any,
    context?: string,
    config: ErrorHandlingConfig = {}
  ): { message: string; fieldErrors: Record<string, string> } {
    const defaultConfig: ErrorHandlingConfig = {
      showValidationDetails: false,
      logToConsole: true,
      genericMessages: {
        validation: 'The form contains invalid data. Please check your input and try again.',
        server: 'An error occurred while processing your request. Please try again.',
        network: 'Unable to connect to the server. Please check your connection and try again.',
        unknown: 'An unexpected error occurred. Please try again.'
      }
    };

    const finalConfig = { ...defaultConfig, ...config };
    const fieldErrors: Record<string, string> = {};

    // Log error details if enabled
    if (finalConfig.logToConsole) {
      console.error(`Error in ${context || 'Unknown'}:`, error);
    }

    // Handle different HTTP status codes
    switch (error.status) {
      case 400: // Bad Request
        if (error.error?.detail) {
          return {
            message: error.error.detail,
            fieldErrors
          };
        }
        return {
          message: finalConfig.genericMessages!.server!,
          fieldErrors
        };

      case 401: // Unauthorized
        return {
          message: 'You are not authorized to perform this action. Please log in again.',
          fieldErrors
        };

      case 403: // Forbidden
        return {
          message: 'You do not have permission to perform this action.',
          fieldErrors
        };

      case 404: // Not Found
        // Preserve the original error detail if available, otherwise use generic message
        if (error.error?.detail) {
          return {
            message: error.error.detail,
            fieldErrors
          };
        }
        return {
          message: 'The requested resource was not found.',
          fieldErrors
        };

      case 409: // Conflict
        if (typeof error.error?.detail === 'string' && error.error.detail.trim()) {
          return {
            message: error.error.detail.trim(),
            fieldErrors
          };
        }
        return {
          message: 'This resource conflicts with existing data. Please check your input.',
          fieldErrors
        };

      case 422: // Unprocessable Entity (Validation Error)
        if (error.error?.detail) {
          // Log detailed validation errors for debugging
          if (finalConfig.logToConsole) {
            console.error('422 Validation Error Details:', error.error.detail);
          }

          // Parse field-specific errors if available
          if (Array.isArray(error.error.detail)) {
            error.error.detail.forEach((detail: any) => {
              if (detail.loc && detail.msg) {
                const fieldName = detail.loc[detail.loc.length - 1];
                fieldErrors[fieldName] = detail.msg;
              }
            });
          }

          // Create a more helpful message if we have field errors
          let message = finalConfig.genericMessages!.validation!;
          if (Object.keys(fieldErrors).length > 0) {
            const fieldNames = Object.keys(fieldErrors);
            if (fieldNames.length === 1) {
              message = `Validation error: Please check the ${fieldNames[0]} field.`;
            } else {
              message = `Validation errors: Please check the following fields: ${fieldNames.join(', ')}.`;
            }
          }

          return {
            message,
            fieldErrors
          };
        }
        return {
          message: finalConfig.genericMessages!.validation!,
          fieldErrors
        };

      case 429: // Too Many Requests
        return {
          message: 'Too many requests. Please wait a moment and try again.',
          fieldErrors
        };

      case 500: // Internal Server Error
        return {
          message: 'An unexpected server error occurred. Please try again or contact support.',
          fieldErrors
        };

      case 502: // Bad Gateway
      case 503: // Service Unavailable
      case 504: // Gateway Timeout
        // FastAPI often puts a specific reason in `detail` (e.g. crypto / config).
        if (
          typeof error.error?.detail === 'string' &&
          error.error.detail.trim()
        ) {
          return {
            message: error.error.detail.trim(),
            fieldErrors
          };
        }
        return {
          message: 'The service is temporarily unavailable. Please try again later.',
          fieldErrors
        };

      default:
        // Network errors (no status code)
        if (error.status === 0 || error.statusText === 'Unknown Error') {
          return {
            message: finalConfig.genericMessages!.network!,
            fieldErrors
          };
        }

        // Handle other error types
        if (error.error?.detail) {
          return {
            message: error.error.detail,
            fieldErrors
          };
        }

        if (error.error?.message) {
          return {
            message: error.error.message,
            fieldErrors
          };
        }

        return {
          message: finalConfig.genericMessages!.unknown!,
          fieldErrors
        };
    }
  }

  /**
   * Handles form submission errors with loading state management
   * @param error The HTTP error response
   * @param errorState Object containing error signals
   * @param context Optional context for logging
   * @param config Optional configuration for error handling
   */
  handleFormError(
    error: any,
    errorState: ErrorState,
    context?: string,
    config?: ErrorHandlingConfig
  ): void {
    // Reset loading state
    errorState.isLoading.set(false);

    // Handle the error
    const { message, fieldErrors } = this.handleHttpError(error, context, config);

    // Set error messages
    errorState.serverError.set(message);
    errorState.fieldErrors.set(fieldErrors);
  }

  /**
   * Clears all error states
   * @param errorState Object containing error signals
   */
  clearErrors(errorState: ErrorState): void {
    errorState.serverError.set('');
    errorState.fieldErrors.set({});
  }

  /**
   * Gets a user-friendly error message for a specific field
   * @param fieldName The name of the field
   * @param fieldErrors Object containing field-specific errors
   * @returns The error message for the field, or empty string if none
   */
  getFieldError(fieldName: string, fieldErrors: Record<string, string>): string {
    return fieldErrors[fieldName] || '';
  }

  /**
   * Checks if there are any field errors
   * @param fieldErrors Object containing field-specific errors
   * @returns True if there are field errors, false otherwise
   */
  hasFieldErrors(fieldErrors: Record<string, string>): boolean {
    return Object.keys(fieldErrors).length > 0;
  }
}
